import './license-modal.scss';

import { Box, Stack } from '@mui/material';
import { CreateLicenseField, getCreateLicenseFieldProps } from '@converge/data';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  FormCheckBoxField,
  NewFormTextField,
  NewFromSelectField,
  NewRegionFormSelectField,
} from '../../presentation/components';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { LicenseType, useUpsertBrokerageMutation } from '@converge/graphql';
import { useConvergeNavigation, useConvergeSnackBar } from '../../contexts';

import { ApolloError } from '@apollo/client';
import { CreateLicenseFieldsSchema } from './create.license.validation.schema';
import { DialogLayout } from '../../presentation/components/dialog-layout';
import { ILicenses } from './accordion.form-container';
import { NewConvergeButton } from '../../presentation/components/new-button';
import { NewDateField } from '../../presentation';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

interface AddLicenseModalProps {
  onClose: () => void;
  title: string;
  selectedLicense?: ILicenses | null;
  setSelectedLicense?: Dispatch<SetStateAction<ILicenses | null | undefined>>;
}

export const LICENSE_TYPE = [
  { label: 'Property and Casualty', value: LicenseType?.PropertyAndCasualty },
  { label: 'Surplus Line Tax', value: LicenseType?.SurplusLineTax },
];

interface CreateLicenseFields {
  [CreateLicenseField.LicenseTypeUuid]: string | undefined;
  [CreateLicenseField.LicenseNumber]: string | undefined;
  [CreateLicenseField.LicenseRegionUuid]: string | undefined;
  [CreateLicenseField.LicenseExpirationDate]: string | Date | null;
  [CreateLicenseField.perpetual]: boolean;
}

export const AddLicenseModal = ({
  onClose,
  title,
  selectedLicense,
  setSelectedLicense,
}: AddLicenseModalProps) => {
  const formMethods = useForm<CreateLicenseFields>({
    resolver: yupResolver(CreateLicenseFieldsSchema),
    defaultValues: {},
  });

  const { brokerageUuid } = useParams();
  const navigate = useConvergeNavigation();
  const { notify } = useConvergeSnackBar();
  const { loading, upsertEntity } = useUpsertBrokerageMutation();
  const [isDisabled, setIsDisabled] = useState<boolean>(
    selectedLicense?.node?.perpetual ?? false
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const SubmitLicenseHandler = useCallback(
    (): SubmitHandler<CreateLicenseFields> => async (fields) => {
      let LicenseResponse, LicenseResponseErrors;
      const licensesData = {
        entityUuid: brokerageUuid,
        licenses: [
          {
            licenseType: fields.licenseTypeUuid,
            licenseNumber: fields.licenseNumber,
            regionUuid: fields.licenseRegionUuid,
            expirationDate:
              fields.perpetual === false ? fields.licenseExpirationDate : null,
            perpetual: fields.perpetual,
          },
        ],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;
      const selectedLicenseUuid = selectedLicense?.node?.uuid;
      if (selectedLicenseUuid) {
        licensesData.licenses[0].licenseUuid = selectedLicenseUuid;
      }

      try {
        const { data, errors } = await upsertEntity({
          variables: { entityInput: licensesData },
        });
        LicenseResponse = data;
        LicenseResponseErrors = errors;

        notify(
          `Brokerage license has been ${selectedLicense ? 'updated' : 'added'}`,
          {
            variant: 'success',
          }
        );
        onClose();
        navigate.brokerage({ brokerageUuid: brokerageUuid });
      } catch (cause) {
        console.error(
          `Failed to ${
            selectedLicense ? 'update' : 'save'
          } license. Please try again. (${cause})`
        );
        if (cause instanceof ApolloError) {
          LicenseResponseErrors = cause.graphQLErrors;
        }
      }
      const errorMessage = LicenseResponseErrors?.[0]?.message;
      if (errorMessage || !LicenseResponse) {
        notify(
          `Failed to ${
            selectedLicense ? 'update' : 'save'
          } license. Please try again. (${
            errorMessage || 'No data returned by the API'
          })`,
          {
            variant: 'error',
          }
        );
        return;
      }
    },
    [brokerageUuid, navigate, notify, upsertEntity, onClose, selectedLicense]
  );

  useEffect(() => {
    if (selectedLicense) {
      formMethods.setValue(
        CreateLicenseField.LicenseRegionUuid,
        selectedLicense?.node?.region?.uuid
          ? selectedLicense.node.region.uuid
          : ''
      );
      formMethods.setValue(
        CreateLicenseField.LicenseExpirationDate,
        selectedLicense?.node?.expirationDate
          ? selectedLicense.node.expirationDate
          : ''
      );
      formMethods.setValue(
        CreateLicenseField.LicenseTypeUuid,
        selectedLicense?.node?.licenseType
          ? selectedLicense.node.licenseType
          : ''
      );
      formMethods.setValue(
        CreateLicenseField.LicenseNumber,
        selectedLicense?.node?.licenseNumber
          ? selectedLicense.node.licenseNumber
          : ''
      );
      formMethods.setValue(
        CreateLicenseField.perpetual,
        selectedLicense?.node?.perpetual
          ? selectedLicense.node.perpetual
          : false
      );
    } else {
      formMethods.reset();
    }
  }, [formMethods, selectedLicense]);

  return (
    <div>
      <DialogLayout
        data-testid="add-license-modal"
        className="add-brokerage-license-modal"
        sx={{
          overflow: 'hidden',
          '& .MuiPaper-root.MuiDialog-paper': {
            maxWidth: '100px',
            minWidth: '800px',

            overflow: 'hidden !important',
            fontFamily: 'Red Hat Text, Arial, sans-serif',
          },
          '& .MuiDialogContent-root ': {
            overflow: 'hidden !important',
          },
        }}
        open
        onClose={onClose}
        showCloseButton={true}
        onCloseIconClick={onClose}
        header={<Box className="modal-title-brokerage"> {title}</Box>}
      >
        <FormProvider {...formMethods}>
          <Stack className="modal-container">
            <Box className="fields-right">
              <NewFromSelectField
                {...getCreateLicenseFieldProps(
                  CreateLicenseField.LicenseTypeUuid
                )}
                required
                options={LICENSE_TYPE}
                placeholder="Select Type"
                dataTestId="license-type-dropdown"
              />
            </Box>
            <Box className="fields-left">
              <NewFormTextField
                {...getCreateLicenseFieldProps(
                  CreateLicenseField.LicenseNumber
                )}
                required
                placeholder="Enter License Number"
                dataTestId="license-number-input"
              />
            </Box>
          </Stack>
          <Stack className="modal-container">
            <Box className="fields-right">
              <NewRegionFormSelectField
                {...getCreateLicenseFieldProps(
                  CreateLicenseField.LicenseRegionUuid
                )}
                required
                dataTestId="license-region-dropdown"
              />
            </Box>
            <Box className="fields-left perpetual-container">
              <NewDateField
                key={
                  formMethods.watch('perpetual')
                    ? 'perpetual-true'
                    : 'perpetual-false'
                }
                {...getCreateLicenseFieldProps(
                  CreateLicenseField.LicenseExpirationDate
                )}
                required
                dataTestId="license-expiration-date-input"
                minDate={today}
                disabled={isDisabled === true}
              />
              <Box style={{ marginLeft: '28px', marginTop: '25px' }}>
                <FormCheckBoxField
                  dataTestId="license-perpetual-checkbox"
                  customFont="14px"
                  name={CreateLicenseField.perpetual}
                  label="Perpetual"
                  disabled={false}
                  handleChange={(event) => {
                    const checked = event.target.checked;
                    setIsDisabled(checked);
                    formMethods.setValue(CreateLicenseField.perpetual, checked);
                    if (checked) {
                      formMethods.setValue(
                        CreateLicenseField.LicenseExpirationDate,
                        null
                      );
                      formMethods.clearErrors(
                        CreateLicenseField.LicenseExpirationDate
                      );
                    }
                  }}
                  checked={isDisabled}
                />
              </Box>
            </Box>
          </Stack>
          <Stack className="button-container-brokerage-license ">
            <Box className="close-btn-Container">
              <NewConvergeButton
                label="CANCEL"
                variant="outlined"
                onClick={onClose}
                data-testid="cancel-license-button"
              />
            </Box>
            <Box>
              <NewConvergeButton
                loading={loading}
                onClick={formMethods.handleSubmit(SubmitLicenseHandler())}
                data-testid="submit-license-button"
                label={`${selectedLicense ? 'UPDATE' : 'ADD'} LICENSE`}
              />
            </Box>
          </Stack>
        </FormProvider>
      </DialogLayout>
    </div>
  );
};
